import React, { useEffect, useState } from "react";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  isValidPhoneNumber,
} from "libphonenumber-js";

interface PhoneNumberProps {
  countryCode?: string;
  phoneNumber?: string;
  onChange: (countryCode: string, phoneNumber: string) => void;
  label?: string;
  placeholder?: string;
  isSurvey?: boolean;
  variant?: "default" | "magicLink";
  accentColor?: string;
}

const PhoneNumber: React.FC<PhoneNumberProps> = ({
  countryCode = "+1",
  phoneNumber = "",
  onChange,
  label,
  placeholder = "xxx-xxx-xxxx",
  isSurvey = false,
  variant = "default",
  accentColor = "#39797A",
}) => {
  const [country, setCountry] = useState<string>("US");
  const [localCode, setLocalCode] = useState<string>(countryCode);
  const [localNumber, setLocalNumber] = useState<string>(phoneNumber);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const maxDigitsMap : Record<string, number> = {
    AC:7,AD:9,AE:9,AF:9,AG:7,AI:7,AL:8,AM:8,AO:9,AR:8,AS:7,AT:9,AU:9,AW:7,AZ:9,BA:8,BB:7,BD:9,BE:9,BF:8,BG:8,BH:8,BI:8,BJ:8,BM:7,BN:7,BO:8,BR:11,BS:7,BT:8,BW:8,BY:9,BZ:7,CH:9,CL:9,CN:11,CR:8,CU:8,CV:7,CY:8,CZ:9,DE:11,DK:8,DM:7,EC:9,EE:8,ES:9,ET:9,FI:9,FJ:7,FM:7,FR:9,GA:7,GE:9,GH:9,GI:8,GL:6,GM:7,GN:9,GQ:9,GT:8,GU:7,GW:7,GY:7,HK:8,HR:9,HU:9,ID:11,IE:9,IL:9,IS:7,JO:9,KE:9,KG:9,KH:9,KW:8,LA:9,LB:8,LC:7,LI:7,LK:9,LR:8,LS:8,LT:8,LU:9,LV:8,LY:9,MA:9,MC:8,MD:8,ME:8,MF:7,MG:9,MK:8,ML:8,MM:9,MN:8,MO:8,MQ:9,MR:8,MS:7,MT:8,MU:8,MV:7,MW:8,MY:9,MZ:9,NA:9,NC:6,NE:8,NF:6,NG:11,NI:8,NL:9,NO:8,NR:7,NU:7,NZ:9,OM:8,PA:8,PE:9,PL:9,PT:9,PW:7,PY:9,QA:8,RE:9,RO:9,RS:9,RW:9,SA:9,SB:7,SC:7,SD:9,SE:9,SG:8,SI:8,SJ:8,SK:9,SL:8,SM:8,SN:9,SO:9,SR:7,ST:7,SV:8,SX:7,SY:9,SZ:8,TC:7,TD:8,TG:8,TH:9,TJ:9,TK:7,TL:8,TM:8,TN:8,TO:7,TT:7,TV:6,TW:9,TZ:9,UA:9,UG:9,UY:8,UZ:9,VA:6,VC:7,VN:9,VU:7,WF:6,WS:7,XK:8,YE:9,YT:9,ZA:9,ZM:9,ZW:9};


  const getMaxDigits = (countryCode: string) => {
    return maxDigitsMap[countryCode] || 10;
  };


  useEffect(() => {
    if (countryCode) setLocalCode(countryCode);
    if (phoneNumber) setLocalNumber(phoneNumber);

     if (!localNumber && !open) {
    let matchedCountry;

    if (countryCode === "+1") {
      matchedCountry = "US";
    } else {
      matchedCountry = getCountries().find(
        (code) => `+${getCountryCallingCode(code)}` === countryCode
      );
    }

    if (matchedCountry) setCountry(matchedCountry);
  }
  }, [countryCode, phoneNumber]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
        setSearchTerm((prev) => prev + e.key.toLowerCase());
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open]);
  useEffect(() => {
    if (!searchTerm) return;
    const timer = setTimeout(() => setSearchTerm(""), 800);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!searchTerm || !open) return;
    const countries = getCountries();
    const match = countries.find((code) =>
      code.toLowerCase().startsWith(searchTerm)
    );

    if (match) {
      const element = document.getElementById(`country-${match}`);
      element?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [searchTerm, open]);

  const handleCountryChange = (newCountry: string) => {
    const newCode = `+${getCountryCallingCode(newCountry as any)}`;
    setCountry(newCountry);
    setLocalCode(newCode);
    setError(null);
    setLocalNumber("");
    onChange(newCode, "");
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const digits = event.target.value.replace(/\D/g, "");
    const maxDigits = getMaxDigits(country);
    const trimmed = digits.slice(0, maxDigits);
    setLocalNumber(trimmed);
    setError(null);
    onChange(localCode, trimmed);
  };

  const handleBlur = () => {
    setFocused(false);
    const maxDigits = getMaxDigits(country);
    if (!localNumber) {
      setError(null);
      return;
    }
    if (localNumber.length < maxDigits) {
      setError(`Phone number must be ${maxDigits} digits long`);
    } else {
      setError(null);
    }
  };

  const validateNumber = (code: string, number: string) => {
     if (!number || number.trim() === "") {
      setError(null);
      return;
    }

    try {
      const full = `${code}${number}`;
      const valid = isValidPhoneNumber(full);
      setError(valid ? null : "Invalid phone number");
    } catch {
      setError("Invalid phone number");
    }
  };

  const labelColor = error ? "#FF5C5C" : "#486484";
  const checkBorder = error
    ? "2px solid #FF5C5C"
    : focused
    ? isSurvey
      ? "2px solid #54A4A6"
      : "2px solid rgba(72, 100, 132, 1)"
    : "none";

  const isMagicLink = variant === "magicLink";
  const magicLinkBorder = error
    ? "1px solid #DC1B09"
    : focused
    ? `1px solid ${accentColor}`
    : "1px solid #F2F2F2";
  const fieldHeight = isMagicLink ? "38px" : isSurvey ? "34px" : "38px";
  const variantBox = isMagicLink
    ? { ...magicLinkStyle.numberBox, border: magicLinkBorder }
    : { border: checkBorder };
  const variantField = isMagicLink ? magicLinkStyle.field : {};

  return (
    <div style={{ display: "flex", flexDirection: "column", position:'relative' }}>
      {label && (
        <label style={{ ...webStyle.labelText, color: labelColor }}>
          {label}
        </label>
      )}
      <div style={{ ...webStyle.numberBox, ...variantBox }}>

        <div style={{ position: "relative", width: "auto" }}>
        <div
          onClick={() => setOpen(!open)}
          style={{
            ...webStyle.countryText,
            ...variantField,
            height: fieldHeight,
          }}
        >
          {country} (+{getCountryCallingCode(country as any)})
          <div style={webStyle.arrow}  onClick={() => setOpen(!open)}>
            <span style={{ top: "-10px", position: "absolute" }}>
              <KeyboardArrowUp style={webStyle.arrowIcon} />
            </span>
            <span style={{ position: "absolute", top: "-4px" }}>
              <KeyboardArrowDown style={webStyle.arrowIcon} />
            </span>
          </div>
        </div>
      </div>

        <div
          style={{
            ...webStyle.divider,
            ...variantField,
            height: fieldHeight,
          }}
        >
          <div style={webStyle.dividerLine} />
        </div>

        <input
          type="tel"
          value={localNumber}
          onChange={handlePhoneChange}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          placeholder={isMagicLink ? "Enter a valid phone number" : "xxx-xxx-xxx"}
          style={{
            ...webStyle.textInput,
            ...variantField,
            height: fieldHeight,
          }}
        />
      </div>
      {open && (
          <ul
            style={webStyle.dropdownList}
          >
            {getCountries().map((code) => (
              <li
              id={`country-${code}`}
                key={code}
                onClick={() => {
                  const newCode = `+${getCountryCallingCode(code)}`;
                  setCountry(code);
                  setLocalCode(newCode);
                  setOpen(false);
                  onChange(newCode, localNumber);
                }}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  backgroundColor: code === country ? "#e2f0ef" : "#fff",
                  color: "#000000",
                  borderBottom:'1px solid #DDDDDD',
                  textAlign:'center'
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f0f0f0")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    code === country ? "#e2f0ef" : "#fff")
                }
              >
                {code} (+{getCountryCallingCode(code)})
              </li>
            ))}
          </ul>
        )}
      {error && (
        <p
          style={
            isMagicLink
              ? { ...webStyle.errorText, ...magicLinkStyle.errorText }
              : webStyle.errorText
          }
        >
          {error}
        </p>
      )}
    </div>
  );
};

export default PhoneNumber;

const webStyle = {
  labelText: {
    marginBottom: "10px",
    fontSize: "0.875rem",
    fontFamily: "Nunito Sans",
    fontWeight: 600,
    color: "#486484",
  },
  numberBox: {
    display: "flex",
    alignItems: "center",
    borderRadius: "0.75rem",
    overflow: "hidden",
    width: "100%",
  },
  countryText: {
    padding: "8px 20px 8px 10px",
    border: "none",
    outline: "none",
    backgroundColor: "#f4f4f4",
    fontSize: "12px",
    fontFamily: "Nunito Sans",
    appearance: "none" as const,
    cursor: "pointer",
    color: "#444545",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  countryCodeInput: {
    width: "60px",
    textAlign: "center" as const,
    border: "none",
    outline: "none",
    backgroundColor: "#f4f4f4",
    fontSize: "1rem",
    fontFamily: "Nunito Sans",
    color: "#4F4F4F",
  },
  arrow: {
    position: "absolute" as const,
    right: -19,
    top: "47%",
    transform: "translateY(-50%)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    pointerEvents: "none" as const,
    fontSize: 10,
    color: "#555",
  },
  arrowIcon: {
    width: "14px",
    color: "#444545",
  },
  divider: {
    background: "#f4f4f4",
    height: "38px",
    paddingLeft: "30px",
    display: "flex",
    alignItems: "center",
  },
  dividerLine: {
    width: "1px",
    height: "16px",
    textAlign: "center" as const,
    background: "#000",
  },
  textInput: {
    flex: 1,
    padding: "8px 10px",
    border: "none" as const,
    outline: "none" as const,
    backgroundColor: "#f4f4f4",
    fontSize: "1rem",
    fontFamily: "Nunito Sans",
    color: "#4F4F4F",
  },
  errorText: {
    color: "#FF5C5C",
    marginTop: "4px",
    marginLeft: "8px",
    fontSize: "0.8rem",
  },
   dropdownList: {
    position: "absolute" as const,
    top: "100%",
    left: 0,
    width: "110px",
    backgroundColor: "#fff",
    borderRadius: "6px",
    marginTop: "2px",
    maxHeight: "240px",
    overflowY: "scroll" as const,
    zIndex: 10,
    listStyle: "none",
    padding: "0",
    border: "1px solid #DDDDDD",

    /* 👇 hide scrollbar but allow scroll */
    scrollbarWidth: "none" as const, // Firefox
  } as React.CSSProperties,

  dropdownItem: {
    padding: "6px 10px",
    cursor: "pointer",
    color: "#000000",
    borderBottom: "1px solid #DDDDDD",
  },
};
const magicLinkStyle = {
  numberBox: {
    borderRadius: "8px",
    boxSizing: "border-box" as const,
    backgroundColor: "#F9FAFC",
  },
  field: {
    boxSizing: "border-box" as const,
    backgroundColor: "#F9FAFC",
    fontSize: "12px",
    color: "#4F4F4F",
  },
  errorText: {
    fontFamily: "Nunito Sans",
    fontSize: "12px",
    lineHeight: "16px",
    color: "#DC1B09",
    marginTop: "2px",
    marginLeft: 0,
  },
};
const style = document.createElement("style");
style.innerHTML = `
  ul::-webkit-scrollbar {
    display: none;
  }
`;
document.head.appendChild(style);
